const normalize = (value) => String(value ?? '').trim().toLowerCase();

const asArray = (value) => (Array.isArray(value) ? value : []);

export const membershipAccountStatus = (membership = {}) => {
  const membershipStatus = normalize(membership.status);
  const user = membership.user || {};
  if (membershipStatus === 'revoked' || membershipStatus === 'removed') return 'removed';
  if (membershipStatus === 'blocked' || user.is_blocked === true) return 'blocked';
  if (membershipStatus === 'suspended' && user.is_active !== false) return 'suspended';
  if (membershipStatus === 'inactive' || user.is_active === false) return 'inactive';
  return 'active';
};

export const mergeUsersWithMemberships = (users = [], memberships = []) => {
  const usersById = new Map(asArray(users).map((user) => [String(user.id || ''), user]));
  const merged = asArray(memberships).map((membership) => {
    const user = membership.user || usersById.get(String(membership.user_id || '')) || {};
    return {
      ...user,
      membership: { ...membership, user: undefined },
      membership_id: membership.id,
      institution_roles: asArray(membership.institution_roles),
      web_enabled: membership.web_enabled === true,
      mobile_enabled: membership.mobile_enabled === true,
      departments_units: Object.prototype.hasOwnProperty.call(membership, 'departments_units')
        ? asArray(membership.departments_units)
        : asArray(user.departments_units),
      enable_nearby_awareness: Object.prototype.hasOwnProperty.call(membership, 'enable_nearby_awareness')
        ? membership.enable_nearby_awareness === true
        : user.enable_nearby_awareness === true,
      account_status: membershipAccountStatus({ ...membership, user }),
    };
  });

  const membershipUserIds = new Set(asArray(memberships).map((row) => String(row.user_id || '')));
  asArray(users).forEach((user) => {
    if (!membershipUserIds.has(String(user.id || ''))) merged.push(user);
  });
  return merged;
};

const tokenized = (query) => String(query || '').match(/(?:[^\s"]+|"[^"]*")+/g) || [];

const stripQuotes = (value) => String(value || '').replace(/^"|"$/g, '');

const departmentLabels = (user) => asArray(user.departments_units)
  .map((department) => normalize(
    typeof department === 'string'
      ? department
      : department?.department_name || department?.name || department?.label || department?.id,
  ))
  .filter(Boolean);

export const userMatchesSmartSearch = (user, query) => {
  const tokens = tokenized(query);
  if (!tokens.length) return true;

  const roles = asArray(user.institution_roles || user.membership?.institution_roles).map(normalize);
  const departments = departmentLabels(user);
  const status = normalize(user.account_status || membershipAccountStatus({
    ...(user.membership || {}),
    user,
  }));
  const access = [
    user.web_enabled === true || user.membership?.web_enabled === true ? 'web' : '',
    user.mobile_enabled === true || user.membership?.mobile_enabled === true ? 'mobile' : '',
  ].filter(Boolean);
  const values = {
    name: normalize(user.full_name || user.name),
    email: normalize(user.email || user.user_account),
    employee: normalize(user.employee_id),
    department: departments.join(' '),
    status,
    role: roles.join(' '),
    access: access.join(' '),
  };
  const freeText = [
    values.name,
    values.email,
    values.employee,
    values.department,
    values.status,
    values.role,
    values.access,
  ].join(' ');

  return tokens.every((rawToken) => {
    const token = stripQuotes(rawToken);
    const separator = token.indexOf(':');
    if (separator > 0) {
      const key = normalize(token.slice(0, separator));
      const value = normalize(stripQuotes(token.slice(separator + 1)));
      if (!value) return true;
      if (key === 'employee_id' || key === 'id') return values.employee.includes(value);
      if (key === 'department' || key === 'unit') return values.department.includes(value);
      if (key === 'role') return roles.some((role) => role.includes(value));
      if (key === 'status') return status === value || status.includes(value);
      if (key === 'access') {
        if (value === 'none') return access.length === 0;
        if (value === 'both') return access.includes('web') && access.includes('mobile');
        return access.includes(value);
      }
      if (Object.prototype.hasOwnProperty.call(values, key)) return values[key].includes(value);
    }
    return freeText.includes(normalize(token));
  });
};

export const filterUsersBySmartSearch = (users, query) => asArray(users)
  .filter((user) => userMatchesSmartSearch(user, query));

export const activationCandidateIds = (users) => asArray(users)
  .filter((user) => normalize(user.account_status) === 'inactive')
  .map((user) => user.membership_id || user.membership?.id)
  .filter(Boolean);

const POST_CLOSE_ROLE_SUBJECT = Object.freeze({
  executive_oversight: 'read_only_reviewer',
  compliance_auditor: 'compliance_auditor',
});

export const postCloseAccessForInstitutionRole = (role, retentionDays = {}) => {
  const subject = POST_CLOSE_ROLE_SUBJECT[normalize(role)];
  if (!subject) return null;
  const rawDays = Number(retentionDays?.[subject]);
  const days = Number.isFinite(rawDays) && rawDays >= 0 ? Math.floor(rawDays) : 0;
  return { subject, days };
};

export const postCloseAccessLabel = (days) => {
  const normalizedDays = Math.max(0, Math.floor(Number(days) || 0));
  if (normalizedDays === 0) return 'Ends immediately when the event closes';
  if (normalizedDays % 365 === 0) {
    const years = normalizedDays / 365;
    return `${years} year${years === 1 ? '' : 's'} (${normalizedDays.toLocaleString()} days)`;
  }
  return `${normalizedDays.toLocaleString()} day${normalizedDays === 1 ? '' : 's'}`;
};

export const normalizeUsersAccessPayload = (payload = {}, fallbackMemberships = [], fallbackInvitations = []) => {
  const memberships = asArray(payload.memberships).length
    ? asArray(payload.memberships)
    : asArray(fallbackMemberships);
  const invitations = asArray(payload.invitations).length
    ? asArray(payload.invitations)
    : asArray(fallbackInvitations);
  const active = memberships.filter((row) => normalize(row.status) === 'active');
  const usage = payload.seat_usage || payload.summary || {};
  const license = payload.license || {};
  const seatLimit = Number(
    usage.named_user_limit
    ?? usage.total_seats
    ?? usage.licensed_total_seats
    ?? usage.limit
    ?? license.licensed_total_seats,
  );
  const namedUsers = Number(usage.named_users ?? usage.used ?? active.length) || 0;

  return {
    memberships,
    invitations,
    seatSummary: {
      used: namedUsers,
      limit: Number.isFinite(seatLimit) ? seatLimit : null,
      available: Number.isFinite(seatLimit)
        ? Math.max(0, Number(usage.available ?? seatLimit - namedUsers))
        : null,
      webEnabled: Number(usage.web_enabled_users ?? active.filter((row) => row.web_enabled).length) || 0,
      mobileEnabled: Number(usage.mobile_enabled_users ?? active.filter((row) => row.mobile_enabled).length) || 0,
      pendingInvitations: Number(
        usage.pending_invitations
        ?? payload.pending_invitations
        ?? invitations.filter((row) => normalize(row.status) === 'pending').length,
      ) || 0,
    },
  };
};
